import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import { Col, Container, Div, Icon, Input, Label, Row, Switch, Text, Textarea } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import OverrideClientTrackingDialog from 'src/components/dialogs/overrideClientTracking';
import { noOp } from 'src/functions';
import { Message, Button } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { appActions, clientsEditActions } from 'src/store/actions';
import { Page, OnboardingConfigs } from 'src/containers';
import Rbac from 'src/containers/rbac';

const defaultValues = {
  billingDay: '',
  billingNote: '',
  billingUsageStartDate: null,
  canEnrich: false,
  canModel: false,
  canPersonalize: false,
  canScore: false,
  contractEndDate: null,
  contractNoticeDate: null,
  contractStartDate: null,
  designation: null,
  emailWhitelist: '',
  enableLegacyPlatform: false,
  enableRealTimeAPICaching: false,
  enableUpgradedPlatform: true,
  hasActivatedClusters: false,
  name: '',
  realTimeAPICachingWindowMinutes: 60,
  reminderStartDate: null,
  shouldNotifyClientServices: false,
  shouldNotifyUsage: false,
  shouldRealTimeModelsDedicated: false,
  shouldSuppressAlerts: false,
  shouldTrackUsage: false,
  shouldTrainSagemaker: true,
  showLegacyPlatformTags: false,
};

function EditClient({
  app: {
    tenant,
    profile: { siteRole },
  },
  clientsEdit: { client, fetchClientStatus, enableRealTimeApiStatus, disableRealTimeApiStatus, editClientStatus },
  disableRealTimeApi,
  editClient,
  enableRealTimeApi,
  enqueueNotification,
  fetchClient,
  history,
  location: { pathname },
  match: {
    params: { clientId: clientIdParam },
  },
}) {
  const { control, errors, handleSubmit, register: registerInput, reset, setValue, watch } = useForm({});

  const [canEnrich, setCanEnrich] = useState(false);
  const [canModel, setCanModel] = useState(false);
  const [canPersonalize, setCanPersonalize] = useState(false);
  const [canScore, setCanScore] = useState(false);
  const [designation, setDesignation] = useState(siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
  const [enableLegacyPlatform, setEnableLegacyPlatform] = useState(false);
  const [enableRealTimeAPICaching, setEnableRealTimeAPICaching] = useState(false);
  const [enableUpgradedPlatform, setEnableUpgradedPlatform] = useState(true);
  const [hasActivatedClusters, setHasActivatedClusters] = useState(false);
  const [overrideTrackingDialogOpen, setOverrideTrackingDialogOpen] = useState(false);
  const [shouldNotifyUsage, setShouldNotifyUsage] = useState(false);
  const [shouldRealTimeModelsDedicated, setShouldRealTimeModelsDedicated] = useState(false);
  const [shouldSuppressAlerts, setShouldSuppressAlerts] = useState(false);
  const [shouldTrackUsage, setShouldTrackUsage] = useState(false);
  const [shouldTrainSagemaker, setshouldTrainSagemaker] = useState(true);
  const [showLegacyPlatformTags, setShowLegacyPlatformTags] = useState(false);
  const [triggeredSubmit, setTriggeredSubmit] = useState(false);
  const formData = watch();

  const onSubmit = useCallback(
    (data) => {
      const shouldTrackNoDate = shouldTrackUsage && [null, undefined, ''].includes(data.billingUsageStartDate);
      const shouldTrackOff = shouldTrackUsage === false;
      if (shouldTrackNoDate || shouldTrackOff) {
        setOverrideTrackingDialogOpen(true);
      } else {
        setTriggeredSubmit(true);
      }
    },
    [setOverrideTrackingDialogOpen, setTriggeredSubmit, shouldTrackUsage]
  );

  const getData = useCallback(() => {
    fetchClient(clientIdParam);
  }, [fetchClient, clientIdParam]);

  useEffect(() => {
    registerInput({ name: 'hasActivatedClusters', type: 'custom' });
    setValue('hasActivatedClusters', false);
    registerInput({ name: 'canEnrich', type: 'custom' });
    setValue('canEnrich', false);
    registerInput({ name: 'canModel', type: 'custom' });
    setValue('canModel', false);
    registerInput({ name: 'canScore', type: 'custom' });
    setValue('canScore', false);
    registerInput({ name: 'canPersonalize', type: 'custom' });
    setValue('canPersonalize', false);
    registerInput({ name: 'canUseAutoLRM', type: 'custom' });
    setValue('canUseAutoLRM', false);
    registerInput({ name: 'canUseHomeLRM', type: 'custom' });
    setValue('canUseHomeLRM', false);
    registerInput({ name: 'canUseFLLitLRM', type: 'custom' });
    setValue('canUseFLLitLRM', false);
    registerInput({ name: 'canUseCLLitLRM', type: 'custom' });
    setValue('canUseCLLitLRM', false);
    registerInput({ name: 'canUseCLRepLRM', type: 'custom' });
    setValue('canUseCLRepLRM', false);
    registerInput({ name: 'shouldTrackUsage', type: 'custom' });
    setValue('shouldTrackUsage', false);
    registerInput({ name: 'shouldNotifyUsage', type: 'custom' });
    setValue('shouldNotifyUsage', false);
    registerInput({ name: 'shouldNotifyClientServices', type: 'custom' });
    setValue('shouldNotifyClientServices', siteRole !== CONSTANTS.ROLE.SUPER_ADMIN);
    registerInput({ name: 'shouldSuppressAlerts', type: 'custom' });
    setValue('shouldSuppressAlerts', false);
    registerInput({ name: 'shouldRealTimeModelsDedicated', type: 'custom' });
    setValue('shouldRealTimeModelsDedicated', false);
    registerInput({ name: 'shouldTrainSagemaker', type: 'custom' });
    setValue('shouldTrainSagemaker', true);
    registerInput({ name: 'designation', type: 'custom' });
    setValue('designation', siteRole === CONSTANTS.ROLE.SUPER_ADMIN);
    registerInput({ name: 'enableUpgradedPlatform', type: 'custom' });
    setValue('enableUpgradedPlatform', true);
    registerInput({ name: 'enableLegacyPlatform', type: 'custom' });
    setValue('enableLegacyPlatform', false);
    registerInput({ name: 'showLegacyPlatformTags', type: 'custom' });
    setValue('showLegacyPlatformTags', false);
    registerInput({ name: 'enableRealTimeAPICaching', type: 'custom' });
    setValue('enableRealTimeAPICaching', false);
    getData();
  }, [getData, registerInput, setValue, siteRole]);

  useEffect(() => {
    setValue('hasActivatedClusters', client.hasActivatedClusters);
    setHasActivatedClusters(client.hasActivatedClusters);
    setValue('canEnrich', client.canEnrich);
    setCanEnrich(client.canEnrich);
    setValue('canModel', client.canModel);
    setCanModel(client.canModel);
    setValue('canScore', client.canScore);
    setCanScore(client.canScore);
    setValue('canPersonalize', client.canPersonalize);
    setCanPersonalize(client.canPersonalize);
    setValue('shouldTrackUsage', client.shouldTrackUsage);
    setShouldTrackUsage(client.shouldTrackUsage);
    setValue('shouldNotifyUsage', client.shouldNotifyUsage);
    setShouldNotifyUsage(client.shouldNotifyUsage);
    setValue('shouldNotifyClientServices', client.shouldNotifyClientServices);
    setValue('shouldSuppressAlerts', client.shouldSuppressAlerts);
    setShouldSuppressAlerts(client.shouldSuppressAlerts);
    setValue('shouldRealTimeModelsDedicated', client.shouldRealTimeModelsDedicated);
    setShouldRealTimeModelsDedicated(client.shouldRealTimeModelsDedicated);
    setValue('shouldTrainSagemaker', client.shouldTrainSagemaker);
    setshouldTrainSagemaker(client.shouldTrainSagemaker);
    setValue('designation', client.designation === 'internal');
    setDesignation(client.designation === 'internal');
    setValue('enableUpgradedPlatform', client.enableUpgradedPlatform);
    setEnableUpgradedPlatform(client.enableUpgradedPlatform);
    setValue('enableLegacyPlatform', client.enableLegacyPlatform);
    setEnableLegacyPlatform(client.enableLegacyPlatform);
    setValue('showLegacyPlatformTags', client.showLegacyPlatformTags);
    setShowLegacyPlatformTags(client.showLegacyPlatformTags);
    setValue('enableRealTimeAPICaching', client.enableRealTimeAPICaching);
    setEnableRealTimeAPICaching(client.enableRealTimeAPICaching);
  }, [client, setValue]);

  useEffect(() => {
    if (enableRealTimeApiStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('There was a problem trying to enable the real time api');
    }
    if (enableRealTimeApiStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('The real time api has been enabled', { autoClose: true });
      setTimeout(() => {
        reset(defaultValues);
      }, 2000);
    }
  }, [enableRealTimeApiStatus, enqueueNotification, reset]);

  useEffect(() => {
    if (disableRealTimeApiStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('There was a problem trying to disable the real time api');
    }
    if (disableRealTimeApiStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('The real time api has been disabled', { autoClose: true });
    }
  }, [disableRealTimeApiStatus, enqueueNotification]);

  useEffect(() => {
    if (editClientStatus === CONSTANTS.API.FAILED) {
      enqueueNotification('Edit client failed');
    }
    if (editClientStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Client successfully updated', { autoClose: true });
    }
  }, [editClientStatus, enqueueNotification]);

  useEffect(() => {
    if (triggeredSubmit) {
      formData.designation = formData.designation ? 'internal' : 'commercial';
      editClient(clientIdParam, formData);
      setTriggeredSubmit(false);
    }
  }, [clientIdParam, editClient, formData, setTriggeredSubmit, triggeredSubmit]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  }, [shouldTrackUsage, shouldNotifyUsage]);

  return (
    <Container minW="100%" w="100%" overflow="auto">
      <Row p={{ x: 0, t: '1rem' }}>
        <Col d="flex" size="6">
          <Button
            buttonStyle="primary"
            onClick={() => {
              history.push('/clients');
            }}
            data-tip="go back"
            data-test="back-btn"
          >
            <Icon color="white" name="LongLeft" m={{ r: '.5em' }} size="1rem" />
            BACK
          </Button>
        </Col>
        <Col d="flex" size="6" flexDir="row-reverse">
          <Div m={{ l: '1rem' }}>
            <Button
              buttonStyle="primary"
              onClick={() => {
                client.isRealTimeApiEnabled
                  ? disableRealTimeApi({ clientTenant: client.tenant })
                  : enableRealTimeApi({ clientTenant: client.tenant });
              }}
              data-tip="Toggle connection to real time api"
              data-test="enable-rtapi-btn"
            >
              {client.isRealTimeApiEnabled ? 'DISABLE REAL TIME API' : 'ENABLE REAL TIME API'}
            </Button>
          </Div>
          <Div m={{ l: '1rem' }}>
            <Button
              buttonStyle="primary"
              onClick={() => history.push(`${pathname}/subscribers`)}
              data-tip="View, add, or remove Pinpoint subscribers from this client"
              data-test="manage-subs-btn"
            >
              MANAGE SUBSCRIBERS
            </Button>
          </Div>
        </Col>
      </Row>
      {
        {
          [CONSTANTS.API.SUCCEEDED]: (
            <>
              <Div bg="white" m={{ y: '1rem' }} p={{ x: '1.5rem', y: '1.25rem' }} rounded="md">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                >
                  {/* Client Name */}
                  <Row p={{ t: '1rem' }} align="center" data-test="name-row">
                    <Col size={{ xs: '12', md: '2' }} data-test="name-label">
                      <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                        <Text textSize="input">Client Name *</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '10' }} data-test="name-value">
                      <Controller
                        name="name"
                        as={Input}
                        control={control}
                        rules={{
                          required: 'Client name is required',
                        }}
                        defaultValue={client.name}
                        data-test="name-input"
                      />
                    </Col>
                  </Row>
                  <Row data-test="name-row-error">
                    <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '10' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="name" />
                      </Text>
                    </Col>
                  </Row>
                  {/* Billing day */}
                  <Row p={{ t: '1rem' }} align="center" data-test="billingDay-row">
                    <Col size={{ xs: '12', md: '2' }} data-test="billingDay-label">
                      <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                        <Text textSize="input">Billing Day *</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '10' }} data-test="billingDay-value">
                      <Controller
                        name="billingDay"
                        as={Input}
                        control={control}
                        rules={{
                          required: 'Billing day is required',
                          min: { value: 1, message: 'Billing day should be between 1 and 28' },
                          max: { value: 28, message: 'Billing day should be between 1 and 28' },
                          pattern: { value: /[0-9]{1,2}/, message: 'Billing day is invalid' },
                        }}
                        defaultValue={client.billingDay}
                        data-test="billingDay-input"
                      />
                    </Col>
                  </Row>
                  <Row data-test="billingDay-row-error">
                    <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '10' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="billingDay" />
                      </Text>
                    </Col>
                  </Row>
                  {/* Email Suffix Whitelist */}
                  <Row p={{ t: '1rem' }} align="center" data-test="emailWhitelist-row">
                    <Col size={{ xs: '12', md: '2' }} data-test="emailWhitelist-label">
                      <Div d="flex" justify="flex-end" align="center" minH="2.5rem">
                        <Text textSize="input">Email Suffix Whitelist</Text>
                        <Icon
                          name="Info"
                          size="1rem"
                          color="brand500"
                          m={{ x: '.5em' }}
                          data-tip={
                            'This field is intended to limit email addresses for a given client\'s users. The whitelist should be a comma-separated list of valid client email suffixes. For example, to limit emails to Gmail and a corporate domain the list would be "gmail.com,corp.com".'
                          }
                        />
                        <Text textSize="input">*</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '10' }} data-test="emailWhitelist-value">
                      <Controller
                        name="emailWhitelist"
                        as={Input}
                        control={control}
                        rules={{
                          required: 'Email whitelist is required',
                          validate: (value) =>
                            !value.includes('*') || 'Email whitelist may only contain exact domain names',
                        }}
                        defaultValue={client.emailWhitelist}
                        data-test="emailWhitelist-input"
                      />
                    </Col>
                  </Row>
                  <Row data-test="emailWhitelist-row-error">
                    <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '10' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="emailWhitelist" />
                      </Text>
                    </Col>
                  </Row>
                  {/* Usage Terms */}
                  <Row p={{ t: '1rem' }} data-test="usageTerms-row">
                    <Col size={{ xs: '12', md: '2' }} data-test="usageTerms-label">
                      <Div d="flex" align="center" minH="2.5rem" justify="flex-end" m={{ t: '-4px' }}>
                        <Text textSize="input">Usage Terms *</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '10' }} data-test="usageTerms-value">
                      <Controller
                        name="billingNote"
                        as={Textarea}
                        control={control}
                        rules={{
                          required: 'Usage terms are required',
                        }}
                        defaultValue={client.billingNote}
                        data-test="usageTerms-input"
                      />
                    </Col>
                  </Row>
                  <Row data-test="usageTerms-row-error">
                    <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="billingNote" />
                      </Text>
                    </Col>
                  </Row>
                  {/* Contract Start Date */}
                  <Row p={{ t: '1rem' }} align="center" data-test="contractStartDate-row">
                    <Col size={{ xs: '12', md: '2' }} data-test="contractStartDate-label">
                      <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                        <Text textSize="input">Contract Start Date</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '10' }} data-test="contractStartDate-value">
                      <Controller
                        name="contractStartDate"
                        as={Input}
                        type="date"
                        control={control}
                        rules={{
                          required: false,
                        }}
                        defaultValue={client.contractStartDate}
                        data-test="contractStartDate-input"
                      />
                    </Col>
                  </Row>
                  <Row data-test="contractStartDate-row-error">
                    <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="contractStartDate" />
                      </Text>
                    </Col>
                  </Row>
                  {shouldTrackUsage && (
                    <>
                      {/* Start Recording Usage */}
                      <Row p={{ t: '1rem' }} align="center" data-test="usageStartDate-row">
                        <Col size={{ xs: '12', md: '2' }} data-test="usageStartDate-label">
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Start Recording Usage</Text>
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', md: '10' }} data-test="usageStartDate-value">
                          <Controller
                            name="billingUsageStartDate"
                            as={Input}
                            type="date"
                            control={control}
                            rules={{
                              required: false,
                            }}
                            defaultValue={client.billingUsageStartDate}
                            data-test="usageStartDate-input"
                          />
                        </Col>
                      </Row>
                      <Row data-test="usageStartDate-row-error">
                        <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                          <Text textColor="danger800" textSize="caption">
                            <ErrorMessage errors={errors} name="billingUsageStartDate" />
                          </Text>
                        </Col>
                      </Row>
                    </>
                  )}
                  {/* Contract End Date */}
                  <Row p={{ t: '1rem' }} align="center" data-test="contractEndDate-row">
                    <Col size={{ xs: '12', md: '2' }} data-test="contractEndDate-label">
                      <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                        <Text textSize="input">Contract End Date</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '10' }} data-test="contractEndDate-value">
                      <Controller
                        name="contractEndDate"
                        as={Input}
                        type="date"
                        control={control}
                        rules={{
                          required: false,
                        }}
                        defaultValue={client.contractEndDate}
                        data-test="contractEndDate-input"
                      />
                    </Col>
                  </Row>
                  <Row data-test="contractEndDate-row-error">
                    <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="contractEndDate" />
                      </Text>
                    </Col>
                  </Row>
                  {/* Client Dates for Notification */}
                  {shouldNotifyUsage && (
                    <>
                      {/* Client Deadline */}
                      <Row p={{ t: '1rem' }} align="center" data-test="contractNoticeDate-row">
                        <Col size={{ xs: '12', md: '2' }} data-test="contractNoticeDate-label">
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Client Deadline</Text>
                            <Icon
                              name="Info"
                              size="1rem"
                              color="brand500"
                              m={{ l: '.5em' }}
                              data-tip={
                                'For POVs, enter the "Commercial Licensing Decision Period" deadline. For Commercial-phase deals, enter the client\'s termination deadline for the current term.'
                              }
                            />
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', md: '10' }} data-test="contractNoticeDate-value">
                          <Controller
                            name="contractNoticeDate"
                            as={Input}
                            type="date"
                            control={control}
                            rules={{
                              required: false,
                            }}
                            defaultValue={client.contractNoticeDate}
                            data-test="contractNoticeDate-input"
                          />
                        </Col>
                      </Row>
                      <Row data-test="contractNoticeDate-row-error">
                        <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                          <Text textColor="danger800" textSize="caption">
                            <ErrorMessage errors={errors} name="contractNoticeDate" />
                          </Text>
                        </Col>
                      </Row>
                      {/* Pinpoint Reminder */}
                      <Row p={{ t: '1rem' }} align="center" data-test="reminderStartDate-row">
                        <Col size={{ xs: '12', md: '2' }} data-test="reminderStartDate-label">
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Pinpoint Reminder</Text>
                            <Icon
                              name="Info"
                              size="1rem"
                              color="brand500"
                              m={{ l: '.5em' }}
                              data-tip="Select a date prior to the Client Deadline to send a reminder email to Pinpoint Client Services and Finance."
                            />
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', md: '10' }} data-test="reminderStartDate-value">
                          <Controller
                            name="reminderStartDate"
                            control={control}
                            as={Input}
                            type="date"
                            rules={{
                              required: false,
                            }}
                            defaultValue={client.reminderStartDate}
                            data-test="reminderStartDate-input"
                          />
                        </Col>
                      </Row>
                      <Row data-test="reminderStartDate-row-error">
                        <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                          <Text textColor="danger800" textSize="caption">
                            <ErrorMessage errors={errors} name="reminderStartDate" />
                          </Text>
                        </Col>
                      </Row>
                    </>
                  )}
                  {enableRealTimeAPICaching && (
                    <>
                      {/* Real-Time API caching window in minutes */}
                      <Row p={{ t: '1rem' }} align="center" data-test="rtapiCachingWindow-row">
                        <Col size={{ xs: '12', md: '2' }} data-test="rtapiCachingWindow-label">
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Real-Time API Caching Window in Minutes</Text>
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', md: '10' }} data-test="rtapiCachingWindow-value">
                          <Controller
                            name="realTimeAPICachingWindowMinutes"
                            as={Input}
                            type="int"
                            control={control}
                            rules={{
                              required: false,
                              min: { value: 1, message: 'Caching window must be positive number' },
                            }}
                            defaultValue={client.realTimeAPICachingWindowMinutes}
                            data-test="rtapiCachingWindow-input"
                          />
                        </Col>
                      </Row>
                      <Row data-test="rtapiCachingWindow-row-error">
                        <Col offset={{ xs: '0', md: '2' }} size={{ xs: '12', md: '8' }}>
                          <Text textColor="danger800" textSize="caption">
                            <ErrorMessage errors={errors} name="realTimeAPICachingWindowMinutes" />
                          </Text>
                        </Col>
                      </Row>
                    </>
                  )}
                  {/* Client Feature/Capability Toggles */}
                  <Row>
                    <Col size={{ xs: '12', md: '4' }}>
                      {/* Can Enrich Onboarded Data */}
                      <Rbac roles={CONSTANTS.ROLES.slice(0, 3)}>
                        <Row p={{ t: '0.75rem' }} align="center" justify="flex-end">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input">Enrich</Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="enrich-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !canEnrich;
                                setCanEnrich(nextValue);
                                setValue('canEnrich', nextValue);
                              }}
                            >
                              <Switch checked={canEnrich} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Can Use Modeling */}
                      <Row p={{ t: '0.75rem' }} align="center" justify="flex-end">
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Model</Text>
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Label
                            tag="div"
                            align="center"
                            justify="flex-end"
                            data-test="model-toggle"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const nextValue = !canModel;
                              setCanModel(nextValue);
                              setValue('canModel', nextValue);
                            }}
                          >
                            <Switch checked={canModel} activeColor="brand500" activeShadow="0" />
                          </Label>
                        </Col>
                      </Row>
                      {/* Can Score Target Segments */}
                      <Row p={{ t: '0.75rem' }} align="center" justify="flex-end">
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Predict</Text>
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Label
                            tag="div"
                            align="center"
                            justify="flex-end"
                            data-test="score-toggle"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const nextValue = !canScore;
                              setCanScore(nextValue);
                              setValue('canScore', nextValue);
                            }}
                          >
                            <Switch checked={canScore} activeColor="brand500" activeShadow="0" />
                          </Label>
                        </Col>
                      </Row>
                      {/* Can Personalize */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center" justify="flex-end">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input">Personalize</Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="personalize-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !canPersonalize;
                                setCanPersonalize(nextValue);
                                setValue('canPersonalize', nextValue);
                              }}
                            >
                              <Switch checked={canPersonalize} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Can Generate Clusters */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input">Cluster IDs</Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="clusters-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !hasActivatedClusters;
                                setHasActivatedClusters(nextValue);
                                setValue('hasActivatedClusters', nextValue);
                              }}
                            >
                              <Switch checked={hasActivatedClusters} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Enable Tigris Refresh or upgraded platform */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Enable Upgraded Platform
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="enable-upgraded-platform-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !enableUpgradedPlatform;
                                setEnableUpgradedPlatform(nextValue);
                                setValue('enableUpgradedPlatform', nextValue);
                              }}
                            >
                              <Switch checked={enableUpgradedPlatform} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Enable Indus Refresh or Legacy Platform */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Enable Legacy Platform
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="enable-legacy-platform-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !enableLegacyPlatform;
                                setEnableLegacyPlatform(nextValue);
                                setValue('enableLegacyPlatform', nextValue);
                              }}
                            >
                              <Switch checked={enableLegacyPlatform} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Show Legacy Platform Tags */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Show Legacy Platform Tags
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="show-legacy-platform-tags-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !showLegacyPlatformTags;
                                setShowLegacyPlatformTags(nextValue);
                                setValue('showLegacyPlatformTags', nextValue);
                              }}
                            >
                              <Switch checked={showLegacyPlatformTags} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                    </Col>
                    <Col size={{ xs: '12', md: '4' }}>
                      {/* Should Report Client Usage */}
                      <Row p={{ t: '0.75rem' }} align="center">
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Report Usage</Text>
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Label
                            tag="div"
                            align="center"
                            justify="flex-end"
                            data-test="track-toggle"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const nextValue = !shouldTrackUsage;
                              setShouldTrackUsage(nextValue);
                              setValue('shouldTrackUsage', nextValue);
                            }}
                          >
                            <Switch checked={shouldTrackUsage} activeColor="brand500" activeShadow="0" />
                          </Label>
                        </Col>
                      </Row>
                      {/* Should Notify Usage */}
                      <Row p={{ t: '0.75rem' }} align="center">
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                            <Text textSize="input">Deadline Reminders</Text>
                          </Div>
                        </Col>
                        <Col size={{ xs: '12', lg: '6' }}>
                          <Label
                            tag="div"
                            align="center"
                            justify="flex-end"
                            data-test="notify-toggle"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const nextValue = !shouldNotifyUsage;
                              setShouldNotifyUsage(nextValue);
                              setValue('shouldNotifyUsage', nextValue);
                            }}
                          >
                            <Switch checked={shouldNotifyUsage} activeColor="brand500" activeShadow="0" />
                          </Label>
                        </Col>
                      </Row>
                      {/* Should Use Sagemaker to train models */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Use SageMaker to train Models
                                <Icon
                                  name="Info"
                                  size="0.75rem"
                                  color="brand500"
                                  m={{ l: '.5em', b: '-0.125rem' }}
                                  data-tip="Turning toggle off trains models using AWS Batch"
                                />
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="train-sagemaker-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !shouldTrainSagemaker;
                                setshouldTrainSagemaker(nextValue);
                                setValue('shouldTrainSagemaker', nextValue);
                              }}
                            >
                              <Switch checked={shouldTrainSagemaker} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Should Suppress Slack Notification */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Suppress Internal Notifications
                                <Icon
                                  name="Info"
                                  size="0.75rem"
                                  color="brand500"
                                  m={{ l: '.5em', b: '-0.125rem' }}
                                  data-tip="Leave off for clients"
                                />
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="suppress-notifications-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !shouldSuppressAlerts;
                                setShouldSuppressAlerts(nextValue);
                                setValue('shouldSuppressAlerts', nextValue);
                              }}
                            >
                              <Switch checked={shouldSuppressAlerts} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Designation */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Internal Client
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="designation-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !designation;
                                setDesignation(nextValue);
                                setValue('shouldNotifyClientServices', !nextValue);
                                setValue('designation', nextValue);
                              }}
                            >
                              <Switch checked={designation} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Should Use Dedicated RT API Infrastructure */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Enable Dedicated Infrastructure for Real-Time API
                                <Icon
                                  name="Info"
                                  size="0.75rem"
                                  color="brand500"
                                  m={{ l: '.5em', b: '-0.125rem' }}
                                  data-tip="Turn on for clients when they're ready to start testing or using real-time API(s)"
                                />
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="dedicated-infrastructure-toggle"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !shouldRealTimeModelsDedicated;
                                setShouldRealTimeModelsDedicated(nextValue);
                                setValue('shouldRealTimeModelsDedicated', nextValue);
                              }}
                            >
                              <Switch checked={shouldRealTimeModelsDedicated} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                      {/* Enable real time api caching */}
                      <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN]}>
                        <Row p={{ t: '0.75rem' }} align="center">
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Div d="flex" align="center" minH="2.5rem" justify="flex-end">
                              <Text textSize="input" style={{ textAlign: 'right', lineHeight: '1.2rem' }}>
                                Enable Real-Time API Caching
                              </Text>
                            </Div>
                          </Col>
                          <Col size={{ xs: '12', lg: '6' }}>
                            <Label
                              tag="div"
                              align="center"
                              justify="flex-end"
                              data-test="enable-real-time-api-caching"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const nextValue = !enableRealTimeAPICaching;
                                setEnableRealTimeAPICaching(nextValue);
                                setValue('enableRealTimeAPICaching', nextValue);
                              }}
                            >
                              <Switch checked={enableRealTimeAPICaching} activeColor="brand500" activeShadow="0" />
                            </Label>
                          </Col>
                        </Row>
                      </Rbac>
                    </Col>
                    <Col size={{ xs: '12', md: '4' }} />
                  </Row>
                  {/* Submit Button */}
                  <Row d="flex" justify="flex-end" p={{ t: '1rem' }}>
                    <Col>
                      <Button buttonStyle="primary" m={{ r: '0rem' }} onClick={handleSubmit(onSubmit)}>
                        <Text color="gray400">SAVE</Text>
                        {editClientStatus === CONSTANTS.API.STARTED ? (
                          <Icon color="gray400" name="Loading" m={{ x: '0.5rem' }} />
                        ) : null}
                      </Button>
                    </Col>
                  </Row>
                </form>
              </Div>
              <OnboardingConfigs tenant={client.tenant} />
            </>
          ),
          [CONSTANTS.API.STARTED]: <Message />,
          [CONSTANTS.API.FAILED]: <Message type="error" />,
          default: <Message type="info" text="No data" cta={{ text: 'Refresh', onClick: getData }} />,
        }[fetchClientStatus]
      }
      <OverrideClientTrackingDialog
        isOpen={overrideTrackingDialogOpen}
        editing
        shouldTrackOff={shouldTrackUsage === false}
        shouldTrackNoDate={shouldTrackUsage && [null, undefined, ''].includes(formData.billingUsageStartDate)}
        actions={{
          close: {
            on: () => setOverrideTrackingDialogOpen(false),
          },
          cancel: { on: noOp },
          handlers: [
            {
              on: () => setTriggeredSubmit(true),
            },
          ],
        }}
      />
    </Container>
  );
}

const mapStateToProps = ({ app, clientsEdit }) => ({ app, clientsEdit });
const mapDispatchToProps = (dispatch) => ({
  fetchClient: (...params) => dispatch(clientsEditActions.fetchClient(...params)),
  enableRealTimeApi: (...params) => dispatch(clientsEditActions.enableRealTimeApi(...params)),
  disableRealTimeApi: (...params) => dispatch(clientsEditActions.disableRealTimeApi(...params)),
  editClient: (...params) => dispatch(clientsEditActions.editClient(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

EditClient.propTypes = {
  fetchClient: PropTypes.func.isRequired,
  enableRealTimeApi: PropTypes.func.isRequired,
  disableRealTimeApi: PropTypes.func.isRequired,
  editClient: PropTypes.func.isRequired,
  clientsEdit: {
    client: PropTypes.shape({}),
    fetchClientStatus: PropTypes.string,
    enableRealTimeApiStatus: PropTypes.string,
    disableRealTimeApiStatus: PropTypes.string,
  }.isRequired,
  app: {
    tenant: PropTypes.string,
    profile: PropTypes.shape({
      siteRole: PropTypes.oneOf([PropTypes.string, null]).isRequired,
    }).isRequired,
  }.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(EditClient));
